// Taking someone to one setting (spec/13 §13): its screen is opened with the setting's key as the URL
// hash — `/o/acme/settings/payments#wallet.minTopupAmount` — so the link can be shared, and the screen
// scrolls the setting's row into view, puts focus in its control and marks it briefly.
//
// The row may not be drawn yet when the screen opens (its view is still loading), so the screen
// waits for it to appear, for a while. Choosing the setting already in the address bar again (the
// router does not see a change) is the palette's `revealSetting` event.

/** The event the palette sends after it navigates: reveal this key on the screen now showing. */
export const REVEAL_SETTING_EVENT = 'clavian:reveal-setting';

/** How long the mark stays on the row. */
export const REVEAL_MARK_MS = 2400;
/** How long the screen waits for the row to be drawn. */
const WAIT_MS = 8000;

/** The key a settings URL's hash names, or undefined. */
export function settingKeyFromHash(hash: string): string | undefined {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (raw === '') return undefined;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/** Ask the screen now showing to reveal `key` (after the palette has navigated to it). */
export function requestRevealSetting(key: string): void {
  window.dispatchEvent(new CustomEvent<string>(REVEAL_SETTING_EVENT, { detail: key }));
}

const FOCUSABLE = [
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[role="switch"]:not([disabled])',
  '[role="combobox"]:not([disabled])',
  'button:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function rowFor(root: ParentNode, key: string): HTMLElement | null {
  for (const element of root.querySelectorAll<HTMLElement>('[data-setting]')) {
    if (element.dataset['setting'] === key) return element;
  }
  return null;
}

/** Scroll the row into view, focus its control (or the row, when it has none to change), and mark it. */
export function revealRow(row: HTMLElement): void {
  const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
  const control = row.querySelector<HTMLElement>(FOCUSABLE);
  if (control === null) {
    // A read-only row (a lock above, or the member's role) is still where the palette said it was.
    if (!row.hasAttribute('tabindex')) row.setAttribute('tabindex', '-1');
    row.focus({ preventScroll: true });
  } else {
    control.focus({ preventScroll: true });
  }
  row.classList.remove('is-revealed');
  // Restart the mark when the same row is revealed twice in a row.
  void row.offsetWidth;
  row.classList.add('is-revealed');
  window.setTimeout(() => row.classList.remove('is-revealed'), REVEAL_MARK_MS);
}

/**
 * Reveal `key`'s row inside `root` as soon as it is drawn. Returns a cancel function, for a screen that
 * goes away (or a new key) before the row arrives.
 */
export function revealWhenDrawn(root: HTMLElement, key: string): () => void {
  const now = rowFor(root, key);
  if (now !== null) {
    revealRow(now);
    return () => undefined;
  }
  const observer = new MutationObserver(() => {
    const row = rowFor(root, key);
    if (row === null) return;
    stop();
    revealRow(row);
  });
  const timer = window.setTimeout(() => stop(), WAIT_MS);
  function stop() {
    observer.disconnect();
    window.clearTimeout(timer);
  }
  observer.observe(root, { childList: true, subtree: true });
  return stop;
}
